import { Fragment, useEffect, useRef, useState, memo } from 'react';
import { Transition } from '@headlessui/react';
import {
  PLAYBACK_SPEED_OPTIONS,
  PlaybackSpeed,
  useRemotionPlayerPlaybackRate
} from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPlaybackRate';
import TooltipV2 from '@/components/TooltipV2';
import Button from '@/components/atoms/Button/Button';
import { classnames } from '@/libs/utils';

function PlaybackSpeedButton() {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { playbackRate, setPlaybackRate } = useRemotionPlayerPlaybackRate();

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const isInsidePopup = menuRef.current?.contains(event.target as Node);
      const isInsideTrigger = triggerRef.current?.contains(event.target as Node);
      if (isInsidePopup || isInsideTrigger) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, { capture: true });
    };
  }, [isOpen]);

  const handleSelect = (speed: PlaybackSpeed) => {
    setPlaybackRate(speed);
    setIsOpen(false);
  };

  const speedLabel = `${playbackRate}×`;

  return (
    <div className="relative">
      <div ref={triggerRef}>
        <TooltipV2
          tooltipId="playback-speed-button"
          tooltipContent="Playback Speed"
          noArrow
          place="top"
          positionStrategy="fixed"
          className="z-10"
          tooltipElement={
            <Button
              className="flex h-[42px] min-w-[42px] items-center justify-center !px-2 text-xs font-semibold"
              variation="checkbox"
              trackingId="playback-speed-button"
              onMouseDown={e => {
                e.stopPropagation();
                setIsOpen(prev => !prev);
              }}
              buttonSize="base"
            >
              {speedLabel}
            </Button>
          }
        />
      </div>
      <Transition
        as={Fragment}
        show={isOpen}
        enter="transition duration-50 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition duration-50 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div
          ref={menuRef}
          className="absolute bottom-12 right-0 z-[100] mb-1 w-44 rounded-2xl border bg-white shadow-lg"
        >
          <div className="p-4">
            <div className="flex flex-col gap-1">
              {PLAYBACK_SPEED_OPTIONS.map(speed => (
                <button
                  key={speed}
                  type="button"
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => handleSelect(speed)}
                  className={classnames(
                    'rounded-lg px-3 py-1.5 text-left text-sm transition-colors hover:bg-slate-100',
                    playbackRate === speed ? 'bg-slate-100 font-semibold' : 'font-normal'
                  )}
                >
                  {speed}×{speed === 1 ? ' (Normal)' : ''}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}

export default memo(PlaybackSpeedButton);
